import type { Property } from '@/types';

interface ProjectionYear {
  year: number;
  propertyValue: number;
  equity: number;
  cumulativeCashflow: number;
  totalWealth: number;
  appreciation: number;
  principalPaydown: number;
}

interface ProjectionSettings {
  appreciationRate: number;
  rentIncreaseRate: number;
  refinanceRate: number;
  yearsToProject: number;
}

export function calculateWealthProjection(
  properties: Property[],
  settings: ProjectionSettings
): ProjectionYear[] {
  const owned = properties.filter(p => p.status === 'in_portfolio' || p.status === 'purchased' || p.status === 'in_renovation');

  if (owned.length === 0) return [];

  const { appreciationRate, rentIncreaseRate, yearsToProject } = settings;
  const projection: ProjectionYear[] = [];

  // Startwerte berechnen
  let totalPropertyValue = owned.reduce(
    (sum, p) => sum + (p.current_value || p.purchase_price || 0),
    0
  );

  let totalLoan = owned.reduce((sum, p) => {
    const activeLoan = (p.financings || [])
      .filter(f => f.is_active)
      .reduce((s, f) => s + f.loan_amount, 0);
    return sum + activeLoan;
  }, 0);

  let monthlyRent = owned.reduce((sum, p) => sum + (p.rent_cold || 0), 0);
  let cumulativeCashflow = 0;

  // Durchschnittliche Finanzierungsparameter
  let avgInterestRate = 0;
  let avgRepaymentRate = 0;
  let totalLoanForAvg = 0;

  owned.forEach(p => {
    (p.financings || []).filter(f => f.is_active).forEach(f => {
      avgInterestRate += f.interest_rate * f.loan_amount;
      avgRepaymentRate += f.repayment_rate * f.loan_amount;
      totalLoanForAvg += f.loan_amount;
    });
  });

  if (totalLoanForAvg > 0) {
    avgInterestRate /= totalLoanForAvg;
    avgRepaymentRate /= totalLoanForAvg;
  }

  // Jahr 0 (heute)
  const initialEquity = totalPropertyValue - totalLoan;
  projection.push({
    year: 0,
    propertyValue: totalPropertyValue,
    equity: initialEquity,
    cumulativeCashflow: 0,
    totalWealth: initialEquity,
    appreciation: 0,
    principalPaydown: 0,
  });

  // Projektion für jedes Jahr
  for (let year = 1; year <= yearsToProject; year++) {
    // Wertsteigerung
    const previousValue = totalPropertyValue;
    totalPropertyValue *= (1 + appreciationRate / 100);
    const yearAppreciation = totalPropertyValue - previousValue;

    // Tilgung (vereinfacht: jährliche Tilgung)
    const annualRepayment = totalLoan * (avgRepaymentRate / 100);
    const previousLoan = totalLoan;
    totalLoan = Math.max(0, totalLoan - annualRepayment);
    const yearPrincipalPaydown = previousLoan - totalLoan;

    // Cashflow berechnen
    const annualRent = monthlyRent * 12;
    const annualInterest = totalLoan * (avgInterestRate / 100);
    const annualPayment = annualInterest + annualRepayment;
    const yearCashflow = annualRent - annualPayment;
    cumulativeCashflow += yearCashflow;

    // Miete erhöhen für nächstes Jahr
    monthlyRent *= (1 + rentIncreaseRate / 100);

    // Equity = Wert - Schulden
    const equity = totalPropertyValue - totalLoan;

    projection.push({
      year,
      propertyValue: totalPropertyValue,
      equity,
      cumulativeCashflow,
      totalWealth: equity + cumulativeCashflow,
      appreciation: yearAppreciation,
      principalPaydown: yearPrincipalPaydown,
    });
  }

  return projection;
}